import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success(`Order marked ${status}`);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markPaid = async (orderId) => {
    try {
      await api.put(`/admin/orders/${orderId}/payment-status`, { payment_status: "paid" });
      toast.success("Payment verified & marked paid");
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, payment_status: "paid", status: "confirmed" } : o))
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-10" data-testid="admin-orders">
      <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Fulfilment</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Orders</h1>

      <div className="mt-8 border border-black/10 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-black/40">
                  No orders yet
                </TableCell>
              </TableRow>
            ) : (
              orders.map((o) => (
                <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                  <TableCell className="font-mono text-xs">#{o.id.slice(0, 8)}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{o.shipping?.full_name}</div>
                    <div className="font-mono text-xs text-black/40">{o.user_email}</div>
                  </TableCell>
                  <TableCell className="font-mono text-[11px] uppercase">
                    {o.method === "upi_qr" ? "UPI QR" : "Razorpay"}
                  </TableCell>
                  <TableCell className="font-mono font-bold">{inr(o.total)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1.5">
                      <Badge
                        className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase ${
                          o.payment_status === "paid"
                            ? "bg-[#14B8A6] text-white"
                            : o.payment_status === "pending_verification"
                            ? "bg-[#34D8A0] text-black"
                            : "bg-black/10 text-black"
                        }`}
                      >
                        {(o.payment_status || "").replace("_", " ")}
                      </Badge>
                      {o.payment_status !== "paid" && (
                        <button
                          onClick={() => markPaid(o.id)}
                          className="font-mono text-[10px] font-semibold uppercase tracking-overline text-[#2F80ED] hover:underline"
                          data-testid={`mark-paid-${o.id}`}
                        >
                          Verify &amp; mark paid
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger className="w-[140px] rounded-none" data-testid={`status-select-${o.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
